/**
 * Protected Layout - Modern Route Protection
 * 
 * This is the BEST PRACTICE approach for protecting routes in React Router v6.
 * Instead of wrapping each route with ProtectedRoute, we create a layout that
 * handles authentication at a higher level.
 * 
 * Benefits:
 * - Cleaner route configuration
 * - Single point of authentication check
 * - No conditional hook calls
 * - Better performance (check once, not per route)
 * - Prevents white screen crashes during auth checks
 * 
 * @author Employee Management System
 * @version 3.0.0
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import AuthenticatedLayout from './AuthenticatedLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import { selectIsAuthReady, selectIsAuthenticated, selectAccessDenied } from '../store/slices/authSlice';

const ProtectedLayout = () => {
  const location = useLocation();
  const isAuthReady = useSelector(selectIsAuthReady);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const accessDenied = useSelector(selectAccessDenied);

  if (!isAuthReady) {
    return <LoadingSpinner fullPage size="lg" text="Oturum kontrol ediliyor..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect before AuthenticatedLayout (and its API-firing children) can mount.
  // This stops the request storm that occurs when a denied user reaches the dashboard.
  if (accessDenied) {
    return <Navigate to="/access-denied" replace />;
  }

  return <AuthenticatedLayout />;
};

export default ProtectedLayout;

